import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';

import * as campaignsApi from '../api/campaignsApi';
import type {
  CampaignDetail,
  CampaignPreview,
  CampaignReconcileResult,
  CampaignSummary,
  CreateCampaignInput,
} from '../model/types';

export const campaignKeys = {
  all: ['campaigns'] as const,
  list: () => ['campaigns', 'list'] as const,
  detail: (id: number) => ['campaigns', 'detail', id] as const,
  preview: (id: number) => ['campaigns', 'preview', id] as const,
};

/**
 * Unieważnienie po zmianie kampanii.
 *
 * ⚠ Zasięg jest **szerszy niż kampania** i to jest celowe: uruchomienie i uzgodnienie **tworzą
 * zobowiązania na cudzych elementach**, więc zmieniają liczby na pulpicie, na listach z menu
 * i przy węzłach drzewka — w miejscach, których ta mutacja nie dotykała wprost. Unieważnienie
 * samej kampanii zostawiłoby na ekranie liczby, o których już wiadomo, że są nieprawdziwe.
 */
function invalidateAfterChange(queryClient: QueryClient) {
  void queryClient.invalidateQueries({ queryKey: campaignKeys.all });
  void queryClient.invalidateQueries({ queryKey: ['commitments'] });
  void queryClient.invalidateQueries({ queryKey: ['elements', 'work-counts'] });
  void queryClient.invalidateQueries({ queryKey: ['elements', 'rollup'] });
}

export function useCampaigns() {
  return useQuery<CampaignSummary[]>({
    queryKey: campaignKeys.list(),
    queryFn: ({ signal }) => campaignsApi.fetchCampaigns(signal),
  });
}

export function useCampaign(id: number) {
  return useQuery<CampaignDetail>({
    queryKey: campaignKeys.detail(id),
    queryFn: ({ signal }) => campaignsApi.fetchCampaign(id, signal),
    enabled: Number.isFinite(id),
  });
}

/**
 * Podgląd „ile trafi" — pobierany tylko wtedy, gdy jest po co (szkic przed uruchomieniem).
 *
 * ⚠ `staleTime: 0`: zbiór liczy się z **żywego grafu**, a podgląd ma odpowiadać na pytanie
 * „co się stanie, jeśli kliknę teraz". Zapamiętana liczba sprzed kwadransa odpowiada na inne.
 */
export function usePreview(id: number, enabled: boolean) {
  return useQuery<CampaignPreview>({
    queryKey: campaignKeys.preview(id),
    queryFn: ({ signal }) => campaignsApi.fetchPreview(id, signal),
    enabled: enabled && Number.isFinite(id),
    staleTime: 0,
  });
}

export function useCreateCampaign() {
  const queryClient = useQueryClient();

  return useMutation<CampaignSummary, unknown, CreateCampaignInput>({
    mutationFn: (input) => campaignsApi.createCampaign(input),
    onSuccess: () => invalidateAfterChange(queryClient),
  });
}

/**
 * Uruchomienie.
 *
 * ⛔ **Bez optimistic UI** — z tego samego powodu, dla którego nie ma go aktywacja konta
 * (decyzja 78): „uruchomione" pokazane przed potwierdzeniem serwera znaczy „wsypałem robotę
 * kilkudziesięciu zespołom", a to nie jest zdanie do cofnięcia rollbackiem.
 */
export function useLaunchCampaign() {
  const queryClient = useQueryClient();

  return useMutation<CampaignDetail, unknown, number>({
    mutationFn: (id) => campaignsApi.launchCampaign(id),
    onSuccess: () => invalidateAfterChange(queryClient),
  });
}

export function useCloseCampaign() {
  const queryClient = useQueryClient();

  return useMutation<CampaignDetail, unknown, number>({
    mutationFn: (id) => campaignsApi.closeCampaign(id),
    onSuccess: () => invalidateAfterChange(queryClient),
  });
}

/**
 * Uzgodnienie na żądanie (decyzja 86).
 *
 * ⭐ Ekran woła je **przy wejściu**, bo skoro ktoś patrzy, to chce świeżego stanu — a dynamika
 * kampanii polega właśnie na tym, że zbiór dogania sam. Przebieg w tle robi to samo bez człowieka;
 * ten wyzwalacz istnieje po to, żeby patrzący nie czekał do najbliższego przebiegu.
 */
export function useReconcileCampaign() {
  const queryClient = useQueryClient();

  return useMutation<CampaignReconcileResult, unknown, number>({
    mutationFn: (id) => campaignsApi.reconcileCampaign(id),
    onSuccess: (result) => {
      // ⚠ Unieważniamy TYLKO wtedy, gdy coś faktycznie doszło. Uzgodnienie przy wejściu na ekran
      // jest w większości przypadków bezowocne (i tak ma być), a bezwarunkowe unieważnienie
      // kazałoby całemu ekranowi pobierać się drugi raz przy każdym wejściu.
      if (result.created > 0) {
        invalidateAfterChange(queryClient);
      }
    },
  });
}
